/** @format */

import {useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

import {SignIn as SignInScreen, type SignInMode} from 'ui';
import {useAuthContext} from 'contexts/AuthContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';
import {useSearchParams, secondLevelDomain} from 'helpers/routerHelpers';

import {useSignInMutation} from './graphql/__generated__';
import {useSignInMutation as useEmailSignInMutation} from '../EmailSignIn/graphql/__generated__';

const SignIn = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useSearchParams();
  const authContext = useAuthContext();
  const {mixpanel} = useMixpanelContext();
  const {t} = useTranslation();

  const initialMode: SignInMode = searchParams.get('mode') === 'email' ? 'email' : 'phone';
  const [mode, setMode] = useState<SignInMode>(initialMode);
  const [error, setError] = useState<string>();

  const [signInPhone, {loading: phoneLoading}] = useSignInMutation();
  const [signInEmail, {loading: emailLoading}] = useEmailSignInMutation();

  const submitState = phoneLoading || emailLoading ? 'submitting' : 'idle';

  const handleModeChange = (next: SignInMode) => {
    setMode(next);
    setError(undefined);
    const search = next === 'email' ? '?mode=email' : '';
    navigate({pathname: '/auth/sign-in', search}, {replace: true, state: location.state});
  };

  const handleSubmit = async ({credential, password}: {credential: string; password: string}) => {
    // `mode` travels with the event: phone and email sign-in used to be
    // separate routes (the channel was readable from Mixpanel's $current_url),
    // one toggled screen now serves both.
    mixpanel.track('click_sign_in_button', {mode});
    setError(undefined);
    // A resolve with no `data` (GraphQL partial-errors, malformed response)
    // used to no-op silently — the button just returned to idle. Surface a
    // generic error so the user gets feedback instead of nothing.
    const noResponse = () =>
      setError(
        t(
          'Не удалось выполнить вход, попробуйте ещё раз',
          'Не удалось выполнить вход, попробуйте ещё раз',
        ),
      );
    try {
      if (mode === 'phone') {
        const {data} = await signInPhone({variables: {phone: credential.trim(), password}});
        if (!data) return noResponse();
        if (data.signIn.user.has2fa && !data.signIn.authToken) {
          navigate('/auth/verify-code', {state: {...location.state, pending2fa: true}});
          return;
        }
        await authContext.signIn(data.signIn.authToken);
      } else {
        const {data} = await signInEmail({variables: {email: credential.trim(), password}});
        if (!data) return noResponse();
        if (data.signInWithEmail.user.has2fa && !data.signInWithEmail.authToken) {
          navigate('/auth/verify-code', {state: {...location.state, pending2fa: true}});
          return;
        }
        await authContext.signIn(data.signInWithEmail.authToken);
      }
      const {from} = location.state || {from: {pathname: '/grouping'}};
      navigate(from, {replace: true});
    } catch (e) {
      if (e instanceof Error) setError(e.message);
    }
  };

  return (
    <SignInScreen
      mode={mode}
      submitState={submitState}
      serverError={error}
      onModeChange={handleModeChange}
      onSubmit={handleSubmit}
      onSignUpClick={() => navigate('/auth/sign-up')}
      onForgotPasswordClick={() =>
        navigate({pathname: '/auth/reset-password', search: mode === 'email' ? '?mode=email' : ''})
      }
      onLogoClick={() => window.location.assign(`https://${secondLevelDomain()}/`)}
      adsensorDomain={secondLevelDomain()}
    />
  );
};

export default SignIn;
